import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { toErrorMessage } from '../../utils/toErrorMessage'
import { CopyTableButtons } from './CopyTableButtons'
import { cell } from './copyTableToClipboard'
import {
  fetchSpendCategory,
  fetchSpendCubeViews,
  fetchSpendDashboard,
  fetchSpendSupplier,
  formatRatio,
  formatSpend,
  formatYoy,
  type SpendCategorySynthesis,
  type SpendCubeViews,
  type SpendDashboard,
  type SpendFilters,
  type SpendSupplierSynthesis,
} from './spendCubeApi'
import { SpendWorkbookPanels } from './SpendWorkbookPanels'
import { EsBadge, EsPageHeader, riskBadgeTone } from './ui'

const selectClass =
  'rounded-[var(--radius-sm)] border border-es-hairline px-2.5 py-2 text-sm bg-es-white text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal'

function bandLabel(band: string | null | undefined): string {
  if (band === 'high') return 'HIGH'
  if (band === 'medium') return 'MEDIUM'
  if (band === 'low') return 'LOW'
  return 'UNSCORED'
}

function Kpi({
  label,
  value,
  to,
  className = 'text-es-navy',
}: {
  label: string
  value: string
  to?: string
  className?: string
}) {
  const body = (
    <>
      <div className={`text-2xl font-bold tabular-nums ${className}`}>{value}</div>
      <div className="text-xs font-semibold text-es-muted uppercase tracking-wider">{label}</div>
    </>
  )
  const skin =
    'rounded-[var(--radius-lg)] px-4 py-3 shadow-[var(--shadow-card)] border border-es-soft bg-es-white'
  if (to) {
    return (
      <Link to={to} className={`${skin} text-left transition-colors hover:border-es-signal hover:bg-es-haze/60 block`}>
        {body}
      </Link>
    )
  }
  return <div className={skin}>{body}</div>
}

export function SpendView() {
  const { isAdmin, effectiveCompanyId } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const yearParam = searchParams.get('year')
  const year = yearParam && /^\d{4}$/.test(yearParam) ? Number(yearParam) : null
  const bu = searchParams.get('bu') || ''
  const rollup = searchParams.get('rollup') || ''
  const category = searchParams.get('category') || ''
  const supplier = searchParams.get('supplier') || ''
  const filters: SpendFilters = { year, bu, rollup }

  const [dashboard, setDashboard] = useState<SpendDashboard | null>(null)
  const [views, setViews] = useState<SpendCubeViews | null>(null)
  const [supplierRow, setSupplierRow] = useState<SpendSupplierSynthesis | null>(null)
  const [categoryRow, setCategoryRow] = useState<SpendCategorySynthesis | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
  }

  const clearAll = () => setSearchParams(new URLSearchParams())

  useEffect(() => {
    if (!isAdmin || !effectiveCompanyId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    const run = async () => {
      const [dash, cubeViews] = await Promise.all([
        fetchSpendDashboard(effectiveCompanyId, filters),
        fetchSpendCubeViews(effectiveCompanyId, filters),
      ])
      if (cancelled) return
      setDashboard(dash)
      setViews(cubeViews)
      if (supplier) {
        setSupplierRow(await fetchSpendSupplier(effectiveCompanyId, supplier, filters))
        setCategoryRow(null)
      } else if (category) {
        setCategoryRow(
          await fetchSpendCategory(effectiveCompanyId, category, { year, bu })
        )
        setSupplierRow(null)
      } else {
        setSupplierRow(null)
        setCategoryRow(null)
      }
    }
    run()
      .catch((err) => {
        if (!cancelled) setError(toErrorMessage(err, 'Failed to load spend.'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // filters object identity changes every render; depend on the fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, effectiveCompanyId, year, bu, rollup, category, supplier])

  const hasLens = Boolean(year || bu || rollup || category || supplier)
  const years = dashboard?.years || []
  const bus = dashboard?.bus || []
  const categories = dashboard?.categories || []

  const openInCube = useRef<(slice: { parent?: string; rollup?: string }) => void>(() => {})

  if (!isAdmin) {
    return (
      <div className="p-4 md:p-6">
        <EsPageHeader title="Spend" description="Spend is available to Simple admins in this preview." />
      </div>
    )
  }

  if (!effectiveCompanyId) {
    return (
      <div className="p-4 md:p-6">
        <EsPageHeader
          title="Spend"
          description="Choose a company with View as. Spend answers stay on that company’s cube."
        />
      </div>
    )
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <EsPageHeader
        className="!mb-3"
        title="Spend"
        description="Invoice spend for this company, joined to the shared supplier risk record. Figures stay on this company’s lines."
      />

      <div className="flex flex-wrap gap-2 items-center">
        <select
          className={selectClass}
          aria-label="Invoice year"
          value={year ?? ''}
          onChange={(e) => setParam('year', e.target.value)}
        >
          <option value="">All years</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          aria-label="Procurement BU"
          value={bu}
          onChange={(e) => setParam('bu', e.target.value)}
        >
          <option value="">All procurement BUs</option>
          {bus.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          aria-label="Roll Up 2"
          value={rollup}
          onChange={(e) => setParam('rollup', e.target.value)}
        >
          <option value="">All categories</option>
          {categories.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        {hasLens && (
          <button type="button" onClick={clearAll} className="text-sm font-semibold text-es-signal hover:underline">
            Clear
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">{error}</div>
      )}

      {loading && !dashboard ? (
        <div className="text-es-muted text-sm">Loading...</div>
      ) : dashboard && !dashboard.hasData ? (
        <p className="text-sm text-es-muted rounded-[var(--radius-md)] border border-es-soft bg-es-haze/60 px-3 py-2">
          No spend file for this company yet.
        </p>
      ) : (
        dashboard && (
          <DashboardBody
            dashboard={dashboard}
            views={views}
            onSupplier={(name) => setParam('supplier', name)}
            onCategory={(name) => setParam('category', name)}
            companyId={effectiveCompanyId}
            pageYear={year}
            pageBu={bu}
            pageRollup={rollup}
            drillFocus={supplier || category}
            bindOpenInCube={openInCube}
            drill={
              supplier ? (
                <SupplierDrill
                  row={supplierRow}
                  loading={loading}
                  onBack={() => setParam('supplier', '')}
                  onOpenInCube={
                    supplierRow?.parentName
                      ? () => openInCube.current({ parent: supplierRow.parentName })
                      : undefined
                  }
                />
              ) : category ? (
                <CategoryDrill
                  row={categoryRow}
                  loading={loading}
                  onBack={() => setParam('category', '')}
                  onSupplier={(name) => setParam('supplier', name)}
                  onOpenInCube={
                    categoryRow?.category && categoryRow.category !== 'Unmapped' && categoryRow.category !== 'Unclassified'
                      ? () => openInCube.current({ rollup: categoryRow.category })
                      : undefined
                  }
                />
              ) : null
            }
          />
        )
      )}
    </div>
  )
}

function DashboardBody({
  dashboard,
  views,
  onSupplier,
  onCategory,
  companyId,
  pageYear,
  pageBu,
  pageRollup,
  drill,
  drillFocus,
  bindOpenInCube,
}: {
  dashboard: SpendDashboard
  views: SpendCubeViews | null
  onSupplier: (name: string) => void
  onCategory: (name: string) => void
  companyId: string
  pageYear: number | null
  pageBu: string
  pageRollup: string
  drill?: ReactNode
  drillFocus?: string
  bindOpenInCube?: { current: (slice: { parent?: string; rollup?: string }) => void }
}) {
  return (
    <SpendWorkbookPanels
      dashboard={dashboard}
      views={views}
      companyId={companyId}
      pageYear={pageYear}
      pageBu={pageBu}
      pageRollup={pageRollup}
      onSupplier={onSupplier}
      onCategory={onCategory}
      drill={drill}
      drillFocus={drillFocus}
      bindOpenInCube={bindOpenInCube}
    />
  )
}

function DrillActions({ onBack, onOpenInCube }: { onBack: () => void; onOpenInCube?: () => void }) {
  return (
    <div className="flex flex-wrap gap-4">
      <button type="button" onClick={onBack} className="text-sm font-semibold text-es-signal hover:underline">
        Back
      </button>
      {onOpenInCube ? (
        <button type="button" onClick={onOpenInCube} className="text-sm font-semibold text-es-signal hover:underline">
          Open in cube
        </button>
      ) : null}
    </div>
  )
}

function SupplierDrill({
  row,
  loading,
  onBack,
  onOpenInCube,
}: {
  row: SpendSupplierSynthesis | null
  loading: boolean
  onBack: () => void
  onOpenInCube?: () => void
}) {
  if (loading && !row) return <div className="text-es-muted text-sm">Loading...</div>
  if (!row?.hasData) {
    const missingCube = row?.reason === 'no_cube'
    return (
      <div className="space-y-3">
        <DrillActions onBack={onBack} />
        <p className="text-sm text-es-muted rounded-[var(--radius-md)] border border-es-soft bg-es-haze/60 px-3 py-2">
          {missingCube ? 'No spend file for this company yet.' : 'No spend for this supplier in the current filters.'}
        </p>
      </div>
    )
  }
  const meta = [row.location, row.region, row.category].filter(Boolean).join(' · ')
  const title = row.supplierName || row.parentName || 'Supplier'
  return (
    <div className="space-y-5">
      <DrillActions onBack={onBack} onOpenInCube={row.parentName ? onOpenInCube : undefined} />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold text-es-navy">{title}</h2>
            {row.linked ? (
              <EsBadge tone={riskBadgeTone(row.band)} className="!text-xs !px-2 !py-0.5">
                {bandLabel(row.band)} RISK
              </EsBadge>
            ) : (
              <EsBadge tone="quiet" className="!text-xs !px-2 !py-0.5">
                Not linked to an Early Signals supplier
              </EsBadge>
            )}
          </div>
          {meta && <p className="text-sm text-es-muted mt-1">{meta}</p>}
          {row.parentName && row.supplierName && row.parentName !== row.supplierName && (
            <p className="text-xs text-es-muted mt-1">Spend parent: {row.parentName}</p>
          )}
          {row.priorityLine && <p className="text-sm text-es-navy mt-2">{row.priorityLine}</p>}
        </div>
        {row.linked && (
          <div className="text-right">
            <div className="text-3xl font-bold tabular-nums text-es-navy">
              {row.score != null ? row.score.toFixed(1) : '—'}
              <span className="text-base font-medium text-es-muted"> /10</span>
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Invoice spend" value={formatSpend(row.spend)} />
        <Kpi label="Share of spend" value={formatRatio(row.share)} />
        <Kpi label="Year over year" value={formatYoy(row.yoy)} />
        <Kpi label="Categories" value={String(row.categoryCount ?? 0)} />
        <Kpi label="PO utilization" value={formatRatio(row.poUtilization)} />
        <Kpi label="Consultant share" value={formatRatio(row.consultantShare)} />
        <Kpi label="Open mitigations" value={String(row.openMitigations ?? 0)} />
        <Kpi
          label="Single source"
          value={row.singleSource == null ? '—' : row.singleSource ? 'Yes' : 'No'}
        />
      </div>
      {row.linked && (row.topRiskLine || row.outlook) && (
        <section className="rounded-[var(--radius-lg)] border border-es-soft bg-es-white shadow-[var(--shadow-card)] px-4 py-3.5 space-y-2">
          {row.topRiskLine && (
            <p className="text-sm text-es-navy">
              <span className="text-xs font-semibold uppercase tracking-wider text-es-signal mr-2">Top risk</span>
              {row.topRiskLine}
            </p>
          )}
          {row.outlook && <p className="text-sm text-es-body">{row.outlook}</p>}
        </section>
      )}
      {row.linked && row.supplierId && (
        <div className="flex flex-wrap gap-4">
          <Link
            to={`/early-signals/risk-matrix?supplier=${encodeURIComponent(row.supplierId)}`}
            className="text-sm font-semibold text-es-signal hover:underline"
          >
            Open Intelligence view
          </Link>
          <Link
            to={`/early-signals/dashboard?supplier=${encodeURIComponent(row.supplierId)}`}
            className="text-sm font-semibold text-es-signal hover:underline"
          >
            Open Risk & mitigation monitor
          </Link>
        </div>
      )}
    </div>
  )
}

function CategoryDrill({
  row,
  loading,
  onBack,
  onSupplier,
  onOpenInCube,
}: {
  row: SpendCategorySynthesis | null
  loading: boolean
  onBack: () => void
  onSupplier: (name: string) => void
  onOpenInCube?: () => void
}) {
  if (loading && !row) return <div className="text-es-muted text-sm">Loading...</div>
  if (!row?.hasData) {
    return (
      <div className="space-y-3">
        <DrillActions onBack={onBack} />
        <p className="text-sm text-es-muted rounded-[var(--radius-md)] border border-es-soft bg-es-haze/60 px-3 py-2">
          {row?.reason === 'no_cube'
            ? 'No spend file for this company yet.'
            : 'No spend for this category in the current filters.'}
        </p>
      </div>
    )
  }
  const headers = ['Supplier', 'Spend', 'Share', 'Risk']
  const copyRows = (row.suppliers || []).map((s) => [
    cell(s.parentName),
    formatSpend(s.spend),
    formatRatio(s.share),
    bandLabel(s.band),
  ])
  return (
    <div className="space-y-5">
      <DrillActions onBack={onBack} onOpenInCube={onOpenInCube} />
      <h2 className="text-xl font-bold text-es-navy">{row.category}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Invoice spend" value={formatSpend(row.spend)} />
        <Kpi label="Share of company" value={formatRatio(row.share)} />
        <Kpi label="Year over year" value={formatYoy(row.yoy)} />
        <Kpi label="Suppliers" value={String(row.supplierCount ?? 0)} />
        <Kpi label="Top supplier share" value={formatRatio(row.topSupplierShare)} />
        <Kpi label="Spend-weighted risk" value={row.weightedRisk != null ? row.weightedRisk.toFixed(1) : '—'} />
      </div>
      <section className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-bold text-es-navy uppercase tracking-wider">Suppliers</h3>
          <CopyTableButtons payload={{ headers, rows: copyRows }} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse min-w-[32rem]">
            <thead className="sticky top-0 bg-es-haze">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="px-2 py-2 font-semibold uppercase tracking-wider text-xs text-es-muted border-b border-es-soft">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(row.suppliers || []).map((s) => (
                <tr key={s.parentName} className="border-b border-es-soft/60">
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      className="font-medium text-es-navy hover:text-es-signal hover:underline text-left"
                      onClick={() => onSupplier(s.parentName)}
                    >
                      {s.parentName}
                    </button>
                  </td>
                  <td className="px-2 py-2 tabular-nums">{formatSpend(s.spend)}</td>
                  <td className="px-2 py-2 tabular-nums">{formatRatio(s.share)}</td>
                  <td className="px-2 py-2">
                    <EsBadge tone={riskBadgeTone(s.band)} className="!text-xs !px-2 !py-0.5">
                      {bandLabel(s.band)}
                    </EsBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
